import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import "cropperjs/dist/cropper.css";
import React, {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Cropper, ReactCropperElement } from "react-cropper";
import { ColumnDefinition } from "tabulator-tables";
import { Instruments } from "../../api/compute/Instruments";
import { Logos } from "../../api/compute/Logos";
import { Structures } from "../../api/compute/Structures";
import { MarketCapFilter } from "../../components/MarketCapFilter/MarketCapFilter";
import Tabulator, { EventsType } from "../../components/Tabulator";
import { useAuth } from "../../hooks/useAuth";
import TreeCheckboxWidget from "./widgets/CountryFilterWidget";
import CloseIcon from "@mui/icons-material/Close";
import { useFeedbackSystem } from "../../hooks/useFeedbackSystem";
const initStockClassFilter = {
  ADR: false,
  STOCK: false,
  GDR: false,
  INVTRUST: false,
  MLP: false,
  OTHER: false,
};
const formatNumber = (num) => {
  if (num >= 1e15) {
    return `${(num / 1e15).toFixed(2)}Q`; // Quadrillions
  } else if (num >= 1e12) {
    return `${(num / 1e12).toFixed(2)}T`; // Trillions
  } else if (num >= 1e9) {
    return `${(num / 1e9).toFixed(2)}B`; // Billions
  } else if (num >= 1e6) {
    return `${(num / 1e6).toFixed(2)}M`; // Millions
  } else if (num >= 1e3) {
    return `${(num / 1e3).toFixed(2)}K`; // Thousands
  } else {
    return num.toString();
  }
};
export default function LogoManager() {
  const auth = useAuth();
  const feedback = useFeedbackSystem();
  const logosAPI = useMemo(() => new Logos(), []);
  const instrumemntsAPI = useMemo(() => new Instruments(), []);
  const structuresAPI = useMemo(() => new Structures(), []);

  //#region -- SECRUTITY TABLE HANDLING
  const [security, setSecurity] = useState<any>(null);
  const [screeningData, setScreeningData] = useState([]);
  const [isLoadingScreening, setIsLoadingScreening] = useState(false);
  // const [pages, setPages] = useState(0);
  // const [page, setPage] = useState(1);
  // const numberOfRows = useMemo(() => 100, []);
  const getScreening = useCallback(
    async (filters, _page?) => {
      setIsLoadingScreening(true);
      const body = {
        filters: filters,
        // page: { page: _page ?? 1, rows: numberOfRows },
        sort: [{ dimension: "marketcap", rev: true }],
      };

      const response = await instrumemntsAPI.screening(body);
      // if (response.dataTotalCount > numberOfRows) {
      //   const _pages = response.dataTotalCount / numberOfRows;
      //   setPages(Math.round(_pages));
      //   setShowPagination(true);
      // } else {
      //   setShowPagination(false);
      // }
      const properties: any = [
        { date: null, property: "type" },
        { date: null, property: "name" },
        { date: null, property: "ticker" },
        { date: null, property: "marketcap" },
      ];

      const fetchRows = await instrumemntsAPI.fetch(
        {
          properties,
          symbols: response?.data ?? [],
          type: "security",
        },
        true
      );
      setScreeningData(fetchRows.data);
      setIsLoadingScreening(false);
      setShowSecurityTable(true);
    },
    [instrumemntsAPI]
  );
  const tableRef = useRef<any>(null);
  const columns: ColumnDefinition[] = useMemo(
    () => [
      {
        title: "Ticker",
        field: "ticker",
        widthGrow:1
      },
      { title: "Name", field: "name", widthGrow:2 },
      {
        title: "Market Cap.",
        field: "marketcap",
        sorter: "number",
        formatter: (cell) => {
          return formatNumber(cell.getValue());
        },
        widthGrow:1
      },
    ],
    []
  );
  const tableOptions = useMemo(
    () => ({
      columns: columns,
      layout: "fitColumns",
      selectableRows: 1,
    }),
    [columns]
  );
  //#endregion

  const [logo, setLogo] = useState<string | null>(null);
  const [hasLogo, setHasLogo] = useState<boolean | null>(null);
  const [checkingLogo, setCheckingLogo] = useState(false);
  const [imageToEdit, setImageToEdit] = useState<any>(null);
  const [preview, setPreview] = useState<any>(null);
  const [tableReady, setTableReady] = useState(false);
  const [showSecurityTable, setShowSecurityTable] = useState(false);
  const getLogo = useCallback(
    async (symbol: string) => {
      setCheckingLogo(true);
      const _logo = await instrumemntsAPI.logo({ symbol });
      setCheckingLogo(false);
      if (_logo != null) {
        setHasLogo(true);
      } else {
        setHasLogo(false);
      }
      setLogo(_logo);
    },
    [instrumemntsAPI]
  );
  const onRowClick = useCallback(
    (e, row) => {
      const table = tableRef.current.getTableInstance();
      if (table.getSelectedData().length > 0) {
        let data = row.getData();
        setSecurity(data);
        getLogo(data.symbol);
        setImageToEdit(null);
        setPreview(null);
      } else {
        setSecurity(null);
      }
    },
    [getLogo]
  );
  const tableEvents: EventsType = useMemo(
    () => [
      { event: "rowClick", callback: onRowClick },
      {
        event: "tableBuilt",
        callback: () => {
          setTableReady(true);
        },
      },
    ],
    [onRowClick]
  );

  useEffect(() => {
    if (screeningData && tableReady) {
      if (tableRef.current) {
        const table = tableRef.current.getTableInstance();
        table.initialized && table.setData(screeningData);
      }
    }
  }, [screeningData, tableReady]);

  const checkingLogoLoader = useMemo(
    () => (
      <Box display={"flex"} flexDirection={"column"} gap={1}>
        <CircularProgress
          title="checking..."
          size={60}
          color="secondary"
        ></CircularProgress>
      </Box>
    ),
    []
  );
  const noLogoPlaceholder = useMemo(
    () => (
      <Box
        display={"flex"}
        flexDirection={"column"}
        alignItems={"center"}
        gap={1}
      >
        <img
          draggable={false}
          width={60}
          height={"auto"}
          src={"./img/image.png"}
          alt="logo"
        />
        <Typography>No logo found!</Typography>
      </Box>
    ),
    []
  );

  //#region - IMAGE PROCESSING
  const resizeImage = useCallback((imageUrl: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const desiredWidth = 60;
        const aspectRatio = img.width / img.height;
        const desiredHeight = Math.round(desiredWidth / aspectRatio);
        const canvas = document.createElement("canvas");
        canvas.width = desiredWidth;
        canvas.height = desiredHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          return reject(
            new Error("Impossibile ottenere il contesto del canvas.")
          );
        }
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high"; // "low", "medium", o "high"
        const mimeType: any = imageUrl.match(/^data:(.*?);base64,/);
        ctx.drawImage(img, 0, 0, desiredWidth, desiredHeight);
        // Salva con una qualità superiore (solo per JPEG)
        const quality = mimeType[1] === "image/jpeg" ? 0.95 : undefined;
        const resizedDataUrl = canvas.toDataURL(
          /*mimeType[1]*/ "image/jpeg",
          quality
        );
        resolve(resizedDataUrl);
      };
      img.onerror = (err) => {
        setImageToEdit(null);
        setPreview(null);
        reject(err);
      };
      img.src = imageUrl;
    });
  }, []);
  const dataURLToBlob = useCallback((dataURL) => {
    const parts = dataURL.split(",");
    const base64Data = parts[1];
    const contentType = parts[0].split(":")[1].split(";")[0];
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  }, []);
  const setCustomPreview = useCallback(
    async (data) => {
      if (data) {
        let _data = await resizeImage(data);
        setPreview(_data);
      } else {
        setPreview(null);
      }
    },
    [resizeImage]
  );
  const loadImageFromFile = useCallback(
    async (file: File) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        if (e.target?.result) {
          try {
            setImageToEdit(e.target.result);
            setCustomPreview(e.target.result);
          } catch (e) {
            console.error(
              "Errore nel ridimensionamento dell'immagine dal file:",
              e
            );
          }
        }
      };
      reader.onerror = (err) => {
        console.error("Errore nella lettura del file:", err);
      };
      reader.readAsDataURL(file);
    },
    [setCustomPreview]
  );
  const getImageByUrl = useCallback(
    async (url) => {
      const formData = new FormData();
      const additionalFields = {
        additionalCookies: JSON.stringify(auth.user.additionalCookies),
      };
      for (const key in additionalFields) {
        formData.append(key, additionalFields[key]);
      }
      formData.append("url", url);

      try {
        let response = await logosAPI.proxyImage(formData);
        const res = await fetch(response);
        const blob = await res.blob();
        const file = new File([blob], "downloaded_image", { type: blob.type });
        loadImageFromFile(file);
      } catch (error) {
        feedback.show({
          type: "error",
          message: `could not get the image, try another image!`,
        });
      }
    },
    [auth.user.additionalCookies, feedback, loadImageFromFile, logosAPI]
  );
  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const item = e.dataTransfer.items[0];
      const caseString = (str) => {
        // Potrebbe essere l'URL dell'immagine
        if (str.startsWith("data:image")) {
          // Questa è un'immagine embedded nel data URL
          setImageToEdit(str);
          setCustomPreview(str);
        } else if (str.startsWith("http://") || str.startsWith("https://")) {
          // Caso URL remoto
          getImageByUrl(str);
        } else {
          // Caso in cui non è né http/https né data, potrebbe essere un altro tipo di dato trascinato
          console.error("Tipo di dato non supportato:", str);
        }
      };
      if (item.kind === "file") {
        // Caso: Trascinato un file dal filesystem
        const file = item.getAsFile();
        if (file) {
          await loadImageFromFile(file);
          return;
        }
      } else if (item.kind === "string") {
        // Caso: Spesso trascinando da un browser si ottiene un URL
        item.getAsString(caseString);
      }
      // }
    },
    [getImageByUrl, loadImageFromFile, setCustomPreview]
  );
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const [imageLinkSearch, setImageLinkSearch] = useState("");
  const onChangeImageLinkSearch = useCallback((event) => {
    const value = event.target.value;
    setImageLinkSearch(value);
  }, []);

  const onclickSearchImageLink = useCallback(async () => {
    if (imageLinkSearch.length > 0) {
      getImageByUrl(imageLinkSearch);
    }
  }, [getImageByUrl, imageLinkSearch]);

  const onSearchImageLinkKeyUp = useCallback(
    (event) => {
      if (event.key === "Enter" && imageLinkSearch.length > 0) {
        onclickSearchImageLink();
      }
    },
    [imageLinkSearch.length, onclickSearchImageLink]
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const handleBrowseClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);
  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0];
        await loadImageFromFile(file);
      }
      e.target.value = null!;
    },
    [loadImageFromFile]
  );
  const handleSearchLogo = useCallback(() => {
    const query = encodeURIComponent(
      security?.name + " " + security?.ticker + " logo"
    );
    const url = `https://duckduckgo.com/?q=${query}&t=h_&iar=images`;
    window.open(
      url,
      "_blank",
      "width=800,height=600,scrollbars=yes,resizable=yes"
    );
  }, [security?.name, security?.ticker]);

  const cropperRef = createRef<ReactCropperElement>();
  const onCancelEditing = useCallback(() => setImageToEdit(null), []);

  const getCropedData = useCallback(async () => {
    setCheckingLogo(true);
    setLogo(preview);
    //--
    // Convertiamo il data URL in Blob
    const imageBlob = dataURLToBlob(preview);

    // Creiamo un FormData e vi appendiamo il file
    const formData = new FormData();
    const fileName = security?.symbol + ".jpg";

    formData.append("file", imageBlob, fileName);
    const additionalFields = {
      additionalCookies: JSON.stringify(auth.user.additionalCookies),
    };
    for (const key in additionalFields) {
      formData.append(key, additionalFields[key]);
    }
    const uploadSuccess = (response) => {
      setImageLinkSearch("");
      setCheckingLogo(false);
      feedback.show({
        type: "success",
        message: `${security?.name} - ${security?.ticker} ${response.message}`,
      });
    };
    const uploadError = () => {
      setCheckingLogo(false);
      feedback.show({
        type: "error",
        message: `Error on saving ${security?.name} - ${security?.ticker} logo`,
      });
    };
    logosAPI.uploadLogo(formData).then(uploadSuccess, uploadError);

    //--
    setHasLogo(true);
    setPreview(null);
    setImageToEdit(null);
  }, [
    auth.user.additionalCookies,
    dataURLToBlob,
    feedback,
    logosAPI,
    preview,
    security?.name,
    security?.symbol,
    security?.ticker,
  ]);

  const onCropMove = useCallback(
    (e) => {
      let data = cropperRef.current?.cropper.getCroppedCanvas().toDataURL();
      setCustomPreview(data);
    },
    [cropperRef, setCustomPreview]
  );
  //#endregion

  //#region - FILTER HANDLING
  const [marketCap, setMarketCap] = useState<any>(null);
  const marketCapWidgetRef = useRef<any>(null);
  const clearMarketCapWidget = useCallback(() => {
    if (marketCapWidgetRef.current) {
      marketCapWidgetRef.current.reset();
    }
  }, []);
  const [marketsData, setMarketsData] = useState<any>(null);
  const treeWidgetRef = useRef<any>(null);
  const clearTreeWidget = useCallback(() => {
    if (treeWidgetRef.current) {
      treeWidgetRef.current.reset();
    }
  }, []);
  const getMarketsData = useCallback(async () => {
    let taxonomies = await structuresAPI.taxonomies();
    setMarketsData(taxonomies.MarketsFinancial);
  }, [structuresAPI]);
  useEffect(() => {
    getMarketsData();
  }, [getMarketsData]);
  const [country, setCountry] = useState([]);
  const [isDomestic, setIsDomestic] = useState(false);
  const [isForeign, setIsForeign] = useState(false);
  const onChangeDomesticCheckbox = useCallback((e) => {
    let checked = e.target.checked;
    setIsDomestic(checked);
  }, []);

  const onChangeForeignCheckbox = useCallback((e) => {
    let checked = e.target.checked;
    setIsForeign(checked);
  }, []);

  const [stockClass, setStockClass] = useState(initStockClassFilter);
  const onChangeStockClass = useCallback((e) => {
    const id = e.target.id;
    const checked = e.target.checked;
    setStockClass((prev) => {
      let temp = { ...prev };
      temp[id] = checked;
      return temp;
    });
  }, []);

  const clearFilters = useCallback(() => {
    setIsDomestic(false);
    setIsForeign(false);
    setCountry([]);
    setStockClass(initStockClassFilter);
    setShowSecurityTable(false);
    // setPage(1);
    setSecurity(false);
    setScreeningData([]);
    setSearchValue("");
    setMarketCap(null);
    clearMarketCapWidget();
    clearTreeWidget();
  }, [clearMarketCapWidget, clearTreeWidget]);
  //#endregion

  const filters = useMemo(() => {
    let _filters: any = [];
    const stockClassKeys = Object.keys(stockClass);
    let _stockClass: string[] = [];
    stockClassKeys.forEach((key) => {
      if (stockClass[key]) {
        _stockClass.push(key);
      }
    });
    if (_stockClass.length > 0) {
      _filters.push({
        dimension: "stockclass",
        segments: _stockClass,
      });
    }
    if (isDomestic && !isForeign) {
      _filters.push({
        dimension: "subtype",
        segments: ["Domestic Stock"],
      });
    }
    if (!isDomestic && isForeign) {
      _filters.push({
        dimension: "subtype",
        segments: ["Foreign Stock"],
      });
    }
    _filters.push({
      dimension: "type",
      segments: ["Stock"],
    });

    if (country.length > 0) {
      _filters.push({
        dimension: "country",
        segments: country,
      });
    }
    if (marketCap) {
      if (marketCap.ge || marketCap.le) {
        let cap: any = {};
        if (marketCap.ge) cap[">="] = marketCap.ge * 1e6;
        if (marketCap.le) cap["<="] = marketCap.le * 1e6;
        _filters.push({
          dimension: "marketcap",
          operator: "range",
          segments: [cap],
        });
      }
    }
    return _filters;
  }, [country, isDomestic, isForeign, marketCap, stockClass]);

  const onFilter = useCallback(() => {
    setScreeningData([]);
    getScreening(filters);
  }, [filters, getScreening]);

  // const onPageChange = useCallback(
  //   (e, _page) => {
  //     setScreeningData([]);
  //     getScreening(filters, _page);
  //     // setPage(_page);
  //   },
  //   [filters, getScreening]
  // );
  // const [showPagination, setShowPagination] = useState(false);

  //#region - SEARCH HANDLER
  const [searchValue, setSearchValue] = useState("");
  const onPressSearch = useCallback(async () => {
    if (searchValue.length > 0) {
      setIsLoadingScreening(true);

      const perPage = 100;
      const params: any = {
        constraints: {
          instrumentType: ["Stock"],
          markets: null,
        },
        hasDataTotalCount: false,
        page: 1,
        perPage: perPage,
        query: searchValue,
      };
      try {
        const result = await instrumemntsAPI.searchAndFetch(
          params,
          "security",
          [
            {
              date: null,
              property: "name",
            },
            {
              date: null,
              property: "symbol",
            },
            {
              date: null,
              property: "ticker",
            },
            {
              date: null,
              property: "marketcap",
            },
          ] as any
        );
        setScreeningData(result.data);
        setIsLoadingScreening(false);
        setShowSecurityTable(true);
      } catch (error) {
        setIsLoadingScreening(false);
      }
    }
  }, [instrumemntsAPI, searchValue]);
  const onSearchKeyUp = useCallback(
    (event) => {
      if (event.key === "Enter" && searchValue.length > 0) {
        onPressSearch();
      }
    },
    [onPressSearch, searchValue.length]
  );
  //#endregion
  return (
    <Box
      height={"100%"}
      width={"100%"}
      display={"flex"}
      overflow={"hidden"}
      position={"relative"}
      gap={1}
    >
      <Box
        overflow={"hidden"}
        flex={1}
        maxWidth={"20%"}
        minWidth={"20%"}
        height={"100%"}
        display={"flex"}
        flexDirection={"column"}
        gap={1}
      >
        <Box
          display={"flex"}
          gap={1}
          justifyContent={"space-between"}
          alignItems={"center"}
          width={"100%"}
        >
          <TextField
            placeholder="Search for a Stock (name, ticker, etc...)"
            inputProps={{ title: "search", style: { fontSize: "0.7vw" } }}
            size="small"
            color="secondary"
            fullWidth
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            onKeyUp={onSearchKeyUp}
            InputProps={{
              endAdornment:
                searchValue.length > 0 ? (
                  <InputAdornment position="end">
                    <IconButton color="secondary" onClick={clearFilters}>
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
          />
          <IconButton color="secondary" onClick={onPressSearch}>
            <SearchIcon />
          </IconButton>
        </Box>
        <Divider sx={{ color: "secondary" }} />
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Typography fontSize={"0.9vw"}>Filter</Typography>

          <Box display={"flex"} gap={1}>
            {filters.length > 1 && (
              <Button size="small" onClick={clearFilters}>
                clear filters
              </Button>
            )}
            <Button
              color="secondary"
              endIcon={<FilterAltIcon />}
              onClick={onFilter}
            >
              Filter
            </Button>
            {/* <IconButton color="secondary" size="small" onClick={onFilter}>
              <FilterAltIcon />
            </IconButton> */}
          </Box>
        </Box>
        <Divider />
        <Box display={"flex"}>
          <Box display={"flex"} alignItems={"center"}>
            <input
              type="checkbox"
              id="checkboxDomestic"
              checked={isDomestic}
              onChange={onChangeDomesticCheckbox}
            />
            <label style={{ fontSize: "0.7vw" }} htmlFor="checkboxDomestic">
              Domestic
            </label>
          </Box>
          <Box display={"flex"} alignItems={"center"}>
            <input
              type="checkbox"
              id="checkboxForeign"
              checked={isForeign}
              onChange={onChangeForeignCheckbox}
            />
            <label style={{ fontSize: "0.7vw" }} htmlFor="checkboxForeign">
              Foreign
            </label>
          </Box>
        </Box>
        <Divider sx={{ fontSize: "0.7vw" }} textAlign="left">
          Market Cap.
        </Divider>
        <Box display={"flex"}>
          <MarketCapFilter
            updateValue={(mktCap) => {
              setMarketCap(mktCap);
            }}
            ref={marketCapWidgetRef}
            hasRangeSlider={false}
            initValue={null}
          />
        </Box>
        <Divider sx={{ fontSize: "0.7vw" }} textAlign="left">
          Asset class
        </Divider>

        <Box>
          <Box display={"flex"} alignItems={"center"}>
            <input
              type="checkbox"
              id="ADR"
              checked={stockClass.ADR}
              onChange={onChangeStockClass}
            />
            <label style={{ fontSize: "0.7vw" }} htmlFor="ADR">
              ADR
            </label>
          </Box>
          <Box display={"flex"} alignItems={"center"}>
            <input
              type="checkbox"
              id="STOCK"
              onChange={onChangeStockClass}
              checked={stockClass.STOCK}
            />
            <label style={{ fontSize: "0.7vw" }} htmlFor="STOCK">
              Common Stock
            </label>
          </Box>
          <Box display={"flex"} alignItems={"center"}>
            <input
              type="checkbox"
              id="GDR"
              onChange={onChangeStockClass}
              checked={stockClass.GDR}
            />
            <label style={{ fontSize: "0.7vw" }} htmlFor="GDR">
              GDR
            </label>
          </Box>
          <Box display={"flex"} alignItems={"center"}>
            <input
              type="checkbox"
              id="INVTRUST"
              onChange={onChangeStockClass}
              checked={stockClass.INVTRUST}
            />
            <label style={{ fontSize: "0.7vw" }} htmlFor="INVTRUST">
              Inv. Trust
            </label>
          </Box>
          <Box display={"flex"} alignItems={"center"}>
            <input
              type="checkbox"
              id="MLP"
              onChange={onChangeStockClass}
              checked={stockClass.MLP}
            />
            <label style={{ fontSize: "0.7vw" }} htmlFor="MLP">
              MLP
            </label>
          </Box>
          <Box display={"flex"} alignItems={"center"}>
            <input
              type="checkbox"
              id="OTHER"
              onChange={onChangeStockClass}
              checked={stockClass.OTHER}
            />
            <label style={{ fontSize: "0.7vw" }} htmlFor="OTHER">
              Other
            </label>
          </Box>
        </Box>

        <Divider sx={{ fontSize: "0.7vw" }} textAlign="left">
          Country
        </Divider>

        {marketsData != null && (
          <Box overflow={"auto"}>
            <TreeCheckboxWidget
              ref={treeWidgetRef}
              onSelectionChange={(e) => {
                setCountry(e);
              }}
              data={marketsData}
            />
          </Box>
        )}
      </Box>

      <Box
        visibility={showSecurityTable ? "visible" : "hidden"}
        display={"flex"}
        flexDirection={"column"}
        height={"100%"}
        alignItems={"center"}
        flex={1}
        gap={1}
        maxWidth={"25%"}
        minWidth={"25%"}
      >
        <Tabulator ref={tableRef} options={tableOptions} events={tableEvents} />
        {/* {showPagination && (
          <Pagination
            size="small"
            color="secondary"
            sx={{ fontSize: "0.7vw" }}
            page={page}
            onChange={onPageChange}
            count={pages}
          />
        )} */}
      </Box>

      {isLoadingScreening && (
        <Box
          display={"flex"}
          width={"100%"}
          height={"100%"}
          alignItems={"center"}
          justifyContent={"center"}
          flexDirection={"column"}
        >
          <CircularProgress color="secondary" />
          <Typography>Loadin stocks...</Typography>
        </Box>
      )}

      {screeningData.length > 0 && security && (
        <Box
          p={1}
          flex={2}
          maxWidth={"55%"}
          minWidth={"55%"}
          display={"flex"}
          alignItems={"center"}
          justifyContent={"center"}
          flexDirection={"column"}
          gap={3}
          overflow={"auto"}
        >
          {security ? (
            <>
              <Box>
                {checkingLogo && checkingLogoLoader}
                {logo && hasLogo && !checkingLogo && (
                  <img
                    draggable={false}
                    width={60}
                    height={"auto"}
                    src={logo}
                    alt="logo"
                  />
                )}
                {!checkingLogo && logo == null && !hasLogo && noLogoPlaceholder}
              </Box>
              <Box display={"flex"} gap={1}>
                <Box
                  boxShadow={3}
                  p={3}
                  display={"flex"}
                  flexDirection={"column"}
                  alignItems={"center"}
                  borderRadius={2}
                  gap={2}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                >
                  <Typography>
                    Drag and drop an image or click{" "}
                    <span
                      onClick={handleBrowseClick}
                      style={{ cursor: "pointer" }}
                    >
                      <strong>HERE</strong>
                    </span>{" "}
                    to {hasLogo ? "update the" : "upload a new"} logo or{" "}
                    <span
                      onClick={handleSearchLogo}
                      style={{ cursor: "pointer" }}
                    >
                      <strong>SEARCH</strong>
                    </span>{" "}
                    {security?.name} Logo
                  </Typography>

                  <Box display={"flex"} gap={1} width={"100%"}>
                    <TextField
                      fullWidth
                      value={imageLinkSearch}
                      onChange={onChangeImageLinkSearch}
                      placeholder="Insert image link"
                      variant="outlined"
                      color="secondary"
                      onKeyUp={onSearchImageLinkKeyUp}
                    />
                    <IconButton
                      color="secondary"
                      size="small"
                      disabled={imageLinkSearch.length <= 0}
                      onClick={onclickSearchImageLink}
                    >
                      <SearchIcon />
                    </IconButton>
                  </Box>

                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: "none" }} // Nasconde l'input
                    onChange={handleFileChange}
                  />
                </Box>
              </Box>
              {imageToEdit && (
                <Box
                  // maxHeight={500}
                  width={"70%"}
                  position={"relative"}
                  display={"flex"}
                  flexDirection={"column"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  gap={1}
                >
                  <Cropper
                    ref={cropperRef}
                    style={{
                      maxHeight: 400,
                      width: "100%",
                      position: "relative",
                    }}
                    cropmove={onCropMove}
                    zoom={onCropMove}
                    zoomTo={0.5}
                    initialAspectRatio={1}
                    preview=".img-preview"
                    src={imageToEdit}
                    viewMode={1}
                    minCropBoxHeight={10}
                    minCropBoxWidth={10}
                    background={false}
                    responsive={true}
                    autoCropArea={1}
                    checkOrientation={false} // https://github.com/fengyuanchen/cropperjs/issues/671
                    guides={true}
                  />
                  {preview && (
                    <Box
                      border={"solid 1px black"}
                      overflow={"hidden"}
                      height={60}
                      width={60}
                      display={"flex"}
                      justifyContent={"center"}
                      alignItems={"center"}
                    >
                      <img src={preview} alt="preview" />
                    </Box>
                  )}
                  <Box display={"flex"} gap={1}>
                    <Button onClick={getCropedData} color="secondary">
                      Save
                    </Button>
                    {/* <Button onClick={getPreview}>See preview</Button> */}
                    <Button onClick={onCancelEditing}>Cancel</Button>
                  </Box>
                </Box>
              )}
            </>
          ) : (
            <Typography>Select a security</Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
